import type { Matrix } from 'mathjs';
import { matrix, multiply, subtract, add, clone } from 'mathjs';
import { fitBox } from 'fit-box/dist/index';
import { Vertex2d, Box } from './Vertex';
import { get } from 'lodash';
export type ImageDirection = '0' | '1' | '2' | '3';

type X1 = number;
type Y1 = number;
type X2 = number;
type Y2 = number;

export namespace WasmDataStructure {
    // (x2, y1, x1, y2)
    export type PartCord = [X2, Y1, X1, Y2];
    export type LabelCord = [X2, Y1, X1, Y2];
    export type HW = [number, number];
    export type Result = Float32Array;
}

enum PartType {
     label= 'label',
            figure= 'fig',
            'sub label'= 'sub',
            custom_label= 'custom_label',
            part_modify= 'part_modify',
}

export enum EditTypes {
    CUSTOM = 'custom_label',
    PART_MODIFY = 'part_modify'
}

export interface PartRawData {
    id: string;
    text: string;
    code: string;
    representive_label_txt: string;
    // x1: X1;
    // y1: Y1;
    // x2: X2;
    // y2: Y2;
    coordinates: [X1, Y1, X2, Y2];
    type: PartType;
    original_text?: string;
    original_code?: string;
}

export interface Part extends PartRawData {
    _id: string;
    // eslint-disable-next-line no-use-before-define
    originalBox: Box;
    // eslint-disable-next-line no-use-before-define
    renderedPartBox?: Box;
    // eslint-disable-next-line no-use-before-define
    renderedLabelBox?: Box;
}

export interface Figure extends Part {
    offset: { start: number, end: number };
}

export interface ImageV3Info {
    src: string;
    imageId: string;
    image_from: string;
    figure: {
        fig: Figure[],
        image_direction: ImageDirection,
        patent_id: string,
        parts: Part[],
    }
}

export interface ThumbInfo {
    new_id: string;
    id: string;
    editType?: EditTypes;
    deleted: boolean;
            type: PartType|EditTypes,
            "coordinates": Part["coordinates"],
            "code": string,
            "text": string
}

// @ts-ignore
export interface ImageV3InfoProcessed extends Omit<ImageV3Info, ['figure']> {
    figure: {
        fig: Figure[],
        image_direction: ImageDirection,
        patent_id: string,
        parts: (Part|ThumbInfo)[],
    }
}

export enum RelativeFrom {
    canvas = 'canvas',
    rawImage = 'rawImage',
}

export interface ImageResizeInfo {
    originImageCenter: Vertex2d;
    rotatedImageCenter: Vertex2d;
    rotateOriginOffset: Vertex2d;
    naturalWidth: number;
    naturalHeight: number;
    imageRotatedWidth: number;
    imageRotatedHeight: number;
    scaledWidth: number,
    scaledHeight: number,
    scale: number,
    baseScale: number,
    overflowFixScale: number,
    wrapperWidth: number,
    wrapperHeight: number,
}

export const rollTimesMatrixesMap = {
    get(key: number): Matrix {
        // @ts-ignore
        return clone(rollTimesMatrixesMap[key] as Matrix);
    },
    toMathCord: matrix([
        [1, 0],
        [0, -1]
    ]),
    toCssCord: matrix([
        [1, 0],
        [0, -1]
    ]),
    0: matrix([
        [1, 0],
        [0, 1]
    ]),
    1: matrix([
        [0, 1],
        [-1, 0]
    ]),
    2: matrix([
        [-1, 0],
        [0, -1]
    ]),
    3: matrix([
        [0, -1],
        [1, 0]
    ]),
};

export function getImageResizeInfo(wrapperEle:HTMLElement, naturalWidth:number, naturalHeight:number, rotate = 0, _overflowFixScale?:number): ImageResizeInfo {
    const swapWidthHeight = rotate % 2 === 1
    const imageRotatedWidth = swapWidthHeight ? naturalHeight : naturalWidth;
    const imageRotatedHeight = swapWidthHeight ? naturalWidth : naturalHeight;

    // 转过来后其实图突破的框的边界线
    const wrapperWidth = swapWidthHeight ? wrapperEle.clientHeight : wrapperEle.clientWidth;
    const wrapperHeight = swapWidthHeight ? wrapperEle.clientWidth : wrapperEle.clientHeight;
    const { width, } = fitBox({
        boundary: { width: wrapperWidth, height: wrapperHeight, },
        box: { width: imageRotatedWidth, height: imageRotatedHeight, },
    });
    const baseScale = width / imageRotatedWidth;

    const rets = {
        naturalWidth,
        naturalHeight,
        imageRotatedWidth, // 给模型的示意原图宽度
        imageRotatedHeight, // 给模型的示意原图高度
        wrapperWidth,
        wrapperHeight,
    }

    // 当默认翻转的时候，有可能图片延伸出框子，所以要给一个专门为Layout和Image的二次缩放
    // 未来有计算难度的时候，可以考虑把这个去掉或写死成1
    let overflowFixScale;
    if (typeof _overflowFixScale === 'number') { // 如果有传入的值，则使用传入的值
        overflowFixScale = _overflowFixScale;
    } else {
        const above100precentValue = Math.max(imageRotatedWidth / naturalWidth, imageRotatedHeight / naturalHeight);
        overflowFixScale = above100precentValue > 1 ? 1 / above100precentValue : 1 / 1;
    }

    const originImageCenter = new Vertex2d(rets.naturalWidth / 2, rets.naturalHeight / 2)
    const rotatedImageCenter = new Vertex2d(rets.imageRotatedWidth / 2, rets.imageRotatedHeight / 2)
    const rotateOriginOffset = new Vertex2d(rotatedImageCenter.x - originImageCenter.x, rotatedImageCenter.y - originImageCenter.y)

    const finalScale = overflowFixScale * baseScale
    return {
        ...rets,
        scale: finalScale,
        baseScale,
        overflowFixScale: overflowFixScale,
        scaledWidth: imageRotatedWidth * finalScale,
        scaledHeight: imageRotatedHeight * finalScale,
        originImageCenter,
        rotatedImageCenter,
        rotateOriginOffset, // 图片旋转后的原点偏移量
    }
}

export function nextTick(): Promise<void> {
    return new Promise(resolve => {
        setTimeout(resolve, 0);
    });
}

export function toMathCordBase(sourceMatrix: Matrix): Matrix {
    const transTargetMatix = rollTimesMatrixesMap.toMathCord;
    return multiply(transTargetMatix, sourceMatrix);
}

// https://www.wolframalpha.com/input/?i=%285%2C2%29+rotate+-90+degrees+center+%2816%2C3.5%29
export function rotateMatrixByPointMatrix(_m: Matrix, _o: Matrix, rollTimes: number, isCssCord: boolean): Matrix {
    let m = _m;
    let o = _o;
    if (isCssCord) {
        // @ts-ignore
        m = toMathCordBase(_m);
        // @ts-ignore
        o = toMathCordBase(_o);
    }
    // p点相对o的坐标
    const delta = subtract(m, o);
    // 把虚拟圆心⭕️移动到 o,旋转
    const newRelatedCord = multiply(rollTimesMatrixesMap.get(rollTimes % 4), delta);
    // transform回去
    // @ts-ignore
    let newAbsluteCord: Matrix = add(o, newRelatedCord);
    if (isCssCord) {
        newAbsluteCord = multiply(rollTimesMatrixesMap.toCssCord, newAbsluteCord);
    }
    return newAbsluteCord;
}

export function rotateByPointo(box: Box, _o: Vertex2d, rollTimes: number, isCssCord = false): Matrix {
    const o = matrix([
        [_o.x, _o.x],
        [_o.y, _o.y]
    ])
    return rotateMatrixByPointMatrix(box._data, o, rollTimes, isCssCord);
}

export function waitImageLoad(imgElement: HTMLImageElement, src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        if (!imgElement) reject('no have image element');
        imgElement.onload = () => {
            resolve(imgElement);
        };
        imgElement.onerror = reject;
        imgElement.src = src;
    })
}

export function createBoxConnectTwoBoxies(from:Box, to:Box):Box {
    return Box.from2Cords(
        from.center,
        to.center
    )
}

export function getDistanceOfTwoBoxes(box1: Box, box2: Box): number {
    const x = Math.max(box1.x1, box2.x1) - Math.min(box1.x2, box2.x2);
    const y = Math.max(box1.y1, box2.y1) - Math.min(box1.y2, box2.y2);

    const u = Math.max(0, x)
    const v = Math.max(0, y)
    return Math.sqrt(u * u + v * v);
    // return Math.max(x, y);
}

export function hasInfoChanged(info:ThumbInfo) {
    const o_code = get(info, 'originInfoStore.original_code') || get(info, 'original_code');
    const o_text = get(info, 'originInfoStore.original_text') || get(info, 'original_text');
    if(info.deleted) {
        return true;
    }
    if(!o_code || !o_text) {
        return false;
    }
    // @ts-ignore
    return (o_code !== info.code || o_text !== (info.realText || info.text))
}

export function needShowUserEditIcon(info:ThumbInfo) {
    // console.log(info.text,info.editType, info)
    if(info.editType === EditTypes.CUSTOM) {
        return true
    }
    return hasInfoChanged(info);
}


export const isCssAspectRatioSupported = (function() {
    try {
        return CSS.supports('aspect-ratio', '16 / 9')
    } catch (e) {
        return false
    }
}());
